import { createContext, Suspense, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { safeLazy as lazy } from "@/lib/safe-lazy";
import { createPortal } from "react-dom";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { useAuth } from "@/lib/auth";
import { useStaff } from "@/hooks/use-staff";
import StaffModal from "./StaffModal";
import StaffRecordNotFound from "./StaffRecordNotFound";

const StaffEmployeeCard = lazy(() => import("./StaffEmployeeCard"));
const StaffStyles = lazy(() => import("./StaffStyles"));
const EmployeeOverlayContext = createContext<((id:string)=>void)|null>(null);

/** One overlay host for the layout, outside sidebar overflow and every pay form. */
export function StaffEmployeeOverlayProvider({children}:{children:ReactNode}) {
  const {currentWorkspace,user}=useAuth();
  const workspaceId=currentWorkspace?.id;
  const userId=user?.id;
  const [requests,setRequests]=useState<{id:string;workspaceId:string;userId:string;key:number}[]>([]);
  const sequence=useRef(0);
  useEffect(()=>{setRequests([])},[workspaceId,userId]);
  const active=requests.filter(request=>request.workspaceId===workspaceId&&request.userId===userId);
  return <EmployeeOverlayContext.Provider value={id=>{
    if(workspaceId&&userId){const key=++sequence.current;setRequests(old=>[...old,{id,workspaceId,userId,key}])}
  }}>
    {children}
    <LinkedEmployeeNotFound/>
    {active.map(request=><EmployeeOverlayHost key={request.key} id={request.id} close={()=>setRequests(old=>old.filter(item=>item.key!==request.key))}/>)}
  </EmployeeOverlayContext.Provider>;
}

function LinkedEmployeeNotFound() {
  const [location,navigate]=useLocation(),search=useSearch(),staff=useStaff();
  const id=location==="/staff/employees"?new URLSearchParams(search).get("employee"):null;
  const {i18n}=useTranslation(),rtl=i18n.language==="ar";
  if(!id||!staff.data||staff.isLoading||staff.isError||staff.data.employees.some(e=>e.id===id))return null;
  return <div className="staff-ui staff-content staff-employee-overlay" dir={rtl?"rtl":"ltr"} lang={rtl?"ar":"en"}>
    <StaffRecordNotFound onClose={()=>{const params=new URLSearchParams(search);params.delete("employee");navigate(`/staff/employees${params.size?`?${params}`:""}`,{replace:true})}}/>
  </div>;
}

function EmployeeOverlayHost({id,close}:{id:string;close:()=>void}) {
  const staff=useStaff(),{i18n}=useTranslation(),rtl=i18n.language==="ar";
  const [host]=useState(()=>document.createElement("div"));
  const [trigger]=useState(()=>document.activeElement as HTMLElement|null);
  const [mounted,setMounted]=useState(false);
  // Append when opened, not at layout mount: DOM order defines StaffModal's topmost dialog.
  useEffect(()=>{
    document.body.appendChild(host);setMounted(true);
    return()=>{
      host.remove();
      const top=Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')).at(-1);
      if(trigger?.isConnected&&(!top||top.contains(trigger)))trigger.focus();
    };
  },[host,trigger]);
  const employee=!staff.isError?staff.data?.employees.find(e=>e.id===id):undefined;
  const loading=<StaffModal employee onClose={close}><div className="staff-loading table" role="status" aria-label={rtl?"جار التحميل":"Loading"}/></StaffModal>;
  if(!mounted)return null;
  return createPortal(<FocusScope trapped loop asChild onUnmountAutoFocus={event=>event.preventDefault()}><div className="staff-ui staff-content staff-advance-scope staff-employee-overlay" dir={rtl?"rtl":"ltr"} lang={rtl?"ar":"en"} onPointerDown={event=>event.stopPropagation()}>
    <Suspense fallback={loading}><StaffStyles/>
      {staff.isLoading?loading:employee&&staff.data?<StaffEmployeeCard key={id} employee={employee} data={staff.data} onClose={close}/>:<StaffModal employee onClose={close}>
        <h2>{rtl?"غير موجود":"Not found"}</h2>
        <div className="note bad" role="alert">{rtl?"هذا الموظف غير متاح في نطاق صلاحياتك الحالي.":"This employee is not available in your current Staff scope."}</div>
        {staff.isError&&<button type="button" className="btn" onClick={()=>void staff.refetch()}>{rtl?"إعادة المحاولة":"Retry"}</button>}
      </StaffModal>}
    </Suspense>
  </div></FocusScope>,host);
}

export function useStaffEmployeeOverlay() {
  return useContext(EmployeeOverlayContext);
}

/** Native button: no routing, no picker selection, no form submission. */
export default function StaffEmployeeLink({employeeId,children,className="",label}:{employeeId:string;children:ReactNode;className?:string;label?:string}) {
  const open=useStaffEmployeeOverlay();
  return <button type="button" className={`staff-employee-link ${className}`} aria-label={label} onClick={event=>{event.preventDefault();event.stopPropagation();open?.(employeeId)}} onKeyDown={event=>{if(event.key==="Enter"||event.key===" ")event.stopPropagation()}}>{children}</button>;
}
